/**
 * @fileoverview Types relating to the Stanford Graphics Library.
 *
 * Types extracted from the `canvasObjects` map in
 *  src/components/pyodide/GraphicsLib/graphics.ts
 */

export const SUPPORTED_SHAPES = [
  'rectangle',
  'oval',
  'line',
  'image',
  'text',
  'polygon',
] as const;
export type GraphicsShapeType = (typeof SUPPORTED_SHAPES)[number];

/**
 * Represents a rectangle Graphics Object.
 */
export interface Rectangle {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'rectangle'` before use.
   */
  type: 'rectangle';
  /** The x-coordinate of the left side of the rectangle. */
  leftX: number;
  /** The y-coordinate of the top side of the rectangle. */
  topY: number;
  /** The x-coordinate of the right side of the rectangle. */
  rightX: number;
  /** The y-coordinate of the bottom side of the rectangle. */
  bottomY: number;
  /** The fill color of the rectangle. */
  color: string;
  /** The outline color of the rectangle. */
  outline: string;
  /** Whether the rectangle is hidden. Default is false. */
  isHidden?: boolean;
}

/**
 * Represents an oval Graphics Object.
 */
export interface Oval {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'oval'` before use.
   */
  type: 'oval';
  /** The x-coordinate of the left side of the oval. */
  leftX: number;
  /** The y-coordinate of the top side of the oval. */
  topY: number;
  /** The x-coordinate of the right side of the oval. */
  rightX: number;
  /** The y-coordinate of the bottom side of the oval. */
  bottomY: number;
  /** The fill color of the oval. */
  color: string;
  /** The outline color of the oval. */
  outline: string;
  /** Whether the oval is hidden. Default is false. */
  isHidden?: boolean;
}

/**
 * Represents a line Graphics Object.
 */
export interface Line {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'line'` before use.
   */
  type: 'line';
  /** The starting coordinates of the line. Ordered as [x, y]. */
  start: [number, number];
  /** The ending coordinates of the line. Ordered as [x, y]. */
  end: [number, number];
  /** The color of the line. */
  color: string;
  /** Whether the line is hidden. Default is false. */
  isHidden?: boolean;
}

/**
 * Represents an image Graphics Object.
 */
export interface Image {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'image'` before use.
   */
  type: 'image';
  /** The x-coordinate of the top left corner of the image. */
  x: number;
  /** The y-coordinate of the top left corner of the image. */
  y: number;
  /** The URL of the image. */
  url: string;
  /** Optional. Constrains the width of the image. */
  width?: number;
  /** Optional. Constrains the height of the image. */
  height?: number;
  /** Whether the image is hidden. Default is false. */
  isHidden?: boolean;
}

/**
 * Represents a text Graphics Object.
 */
export interface Text {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'text'` before use.
   */
  type: 'text';
  /** The x-coordinate of the anchor point of the text. */
  x: number;
  /** The y-coordinate of the anchor point of the text. */
  y: number;
  /** The text to display. */
  text: string;
  /** The text anchor point. */
  anchor: string;
  /** The color of the text. */
  color: string;
  /** The font size of the text. Either a number or a string like '12px' */
  size: number | string;
  /** The font of the text. */
  font: string;
  /** Whether the text is hidden. Default is false. */
  isHidden?: boolean;
}

/**
 * Represents a polygon Graphics Object.
 */
export interface Polygon {
  /**
   * The shape type.
   * Ensures that at compile time, the type of the shape is known. Typescript
   * cannot infer the type of the shape at runtime, so this type MUST be
   * compared against the string constant `'polygon'` before use.
   */
  type: 'polygon';
  /**
   * A list of numbers describing the vertices of the polygon. The polygon
   * is drawn by connecting the points in order. There must therefore be an
   * even number of elements in the list, with each pair of elements forming
   * an (x, y) coordinate.
   */
  coordinates: number[];
  /** The fill color of the polygon. */
  color: string;
  /** The outline color of the polygon. */
  outline: string;
  /** Whether the polygon is hidden. Default is false. */
  isHidden?: boolean;
}

export type GraphicsShape = Rectangle | Oval | Line | Image | Text | Polygon;

export const isRectangle = (object: unknown): object is Rectangle => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const rectangle = object as Rectangle;
  if (rectangle.type !== 'rectangle') {
    return false;
  } else if (
    typeof rectangle.leftX !== 'number' ||
    typeof rectangle.rightX !== 'number' ||
    typeof rectangle.topY !== 'number' ||
    typeof rectangle.bottomY !== 'number' ||
    typeof rectangle.color !== 'string' ||
    typeof rectangle.outline !== 'string' ||
    (rectangle.isHidden !== undefined &&
      typeof rectangle.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isOval = (object: unknown): object is Oval => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const oval = object as Oval;
  if (oval.type !== 'oval') {
    return false;
  } else if (
    typeof oval.leftX !== 'number' ||
    typeof oval.rightX !== 'number' ||
    typeof oval.topY !== 'number' ||
    typeof oval.bottomY !== 'number' ||
    typeof oval.color !== 'string' ||
    typeof oval.outline !== 'string' ||
    (oval.isHidden !== undefined && typeof oval.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isLine = (object: unknown): object is Line => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const line = object as Line;
  if (line.type !== 'line') {
    return false;
  } else if (
    !Array.isArray(line.start) ||
    line.start.length !== 2 ||
    !line.start.every(coordinate => typeof coordinate === 'number') ||
    !Array.isArray(line.end) ||
    line.end.length !== 2 ||
    !line.end.every(coordinate => typeof coordinate === 'number') ||
    typeof line.color !== 'string' ||
    (line.isHidden !== undefined && typeof line.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isImage = (object: unknown): object is Image => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const image = object as Image;
  if (image.type !== 'image') {
    return false;
  } else if (
    typeof image.x !== 'number' ||
    typeof image.y !== 'number' ||
    typeof image.url !== 'string' ||
    (image.width && typeof image.width !== 'number') ||
    (image.height && typeof image.height !== 'number') ||
    (image.isHidden !== undefined && typeof image.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isText = (object: unknown): object is Text => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const text = object as Text;
  if (text.type !== 'text') {
    return false;
  } else if (
    typeof text.x !== 'number' ||
    typeof text.y !== 'number' ||
    typeof text.text !== 'string' ||
    typeof text.anchor !== 'string' ||
    typeof text.color !== 'string' ||
    (typeof text.size !== 'number' && typeof text.size !== 'string') ||
    typeof text.font !== 'string' ||
    (text.isHidden !== undefined && typeof text.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isPolygon = (object: unknown): object is Polygon => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const polygon = object as Polygon;
  if (polygon.type !== 'polygon') {
    return false;
  } else if (
    !Array.isArray(polygon.coordinates) ||
    polygon.coordinates.length % 2 !== 0 ||
    !polygon.coordinates.every(coordinate => typeof coordinate === 'number') ||
    typeof polygon.color !== 'string' ||
    typeof polygon.outline !== 'string' ||
    (polygon.isHidden !== undefined && typeof polygon.isHidden !== 'boolean')
  ) {
    return false;
  }
  return true;
};

export const isGraphicsShape = (object: unknown): object is GraphicsShape => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const shape = object as GraphicsShape;
  if (typeof shape.type !== 'string') {
    return false;
  }
  if (!SUPPORTED_SHAPES.includes(shape.type)) {
    console.debug('Unknown graphics shape type:', shape.type);
    return false;
  }

  switch (shape.type) {
    case 'rectangle':
      return isRectangle(object);
    case 'oval':
      return isOval(object);
    case 'line':
      return isLine(object);
    case 'image':
      return isImage(object);
    case 'text':
      return isText(object);
    case 'polygon':
      return isPolygon(object);
  }
};

export const areShapesEqual = (
  shape: GraphicsShape,
  otherShape: GraphicsShape,
  compareColors: boolean,
): boolean => {
  if (shape.type !== otherShape.type) {
    return false;
  }
  switch (shape.type) {
    case 'rectangle':
      return areRectanglesEqual(shape, otherShape as Rectangle, compareColors);
    case 'oval':
      return areOvalsEqual(shape, otherShape as Oval, compareColors);
    case 'line':
      return areLinesEqual(shape, otherShape as Line, compareColors);
    case 'image':
      return areImagesEqual(shape, otherShape as Image);
    case 'text':
      return areTextsEqual(shape, otherShape as Text, compareColors);
    case 'polygon':
      return arePolygonsEqual(shape, otherShape as Polygon, compareColors);
  }
};

function normalizeRectOrOval(shape: Rectangle | Oval): Rectangle | Oval {
  const [newLeftX, newRightX] =
    Math.min(shape.leftX, shape.rightX) === shape.leftX
      ? [shape.leftX, shape.rightX]
      : [shape.rightX, shape.leftX];

  const [newTopY, newBottomY] =
    Math.min(shape.topY, shape.bottomY) === shape.topY
      ? [shape.topY, shape.bottomY]
      : [shape.bottomY, shape.topY];

  return {
    leftX: newLeftX,
    topY: newTopY,
    rightX: newRightX,
    bottomY: newBottomY,
    color: shape.color,
    outline: shape.outline,
    type: shape.type,
  };
}

function areRectanglesEqual(
  rectangle: Rectangle,
  otherRectangle: Rectangle,
  compareColors: boolean,
): boolean {
  const rectangleNormalized = normalizeRectOrOval(rectangle);
  const otherRectangleNormalized = normalizeRectOrOval(otherRectangle);

  return (
    rectangleNormalized.leftX === otherRectangleNormalized.leftX &&
    rectangleNormalized.topY === otherRectangleNormalized.topY &&
    rectangleNormalized.rightX === otherRectangleNormalized.rightX &&
    rectangleNormalized.bottomY === otherRectangleNormalized.bottomY &&
    (compareColors
      ? rectangleNormalized.color.toLowerCase() ===
          otherRectangleNormalized.color.toLowerCase() &&
        rectangleNormalized.outline.toLowerCase() ===
          otherRectangleNormalized.outline.toLowerCase()
      : true)
  );
}

function areOvalsEqual(
  oval: Oval,
  otherOval: Oval,
  compareColors: boolean,
): boolean {
  const ovalNormalized = normalizeRectOrOval(oval);
  const otherOvalNormalized = normalizeRectOrOval(otherOval);

  return (
    ovalNormalized.leftX === otherOvalNormalized.leftX &&
    ovalNormalized.topY === otherOvalNormalized.topY &&
    ovalNormalized.rightX === otherOvalNormalized.rightX &&
    ovalNormalized.bottomY === otherOvalNormalized.bottomY &&
    (compareColors
      ? ovalNormalized.color.toLowerCase() ===
          otherOvalNormalized.color.toLowerCase() &&
        ovalNormalized.outline.toLowerCase() ===
          otherOvalNormalized.outline.toLowerCase()
      : true)
  );
}

function areLinesEqual(
  line: Line,
  otherLine: Line,
  compareColors: boolean,
): boolean {
  const standardMapIsEqual: boolean =
    line.start[0] === otherLine.start[0] &&
    line.start[1] === otherLine.start[1] &&
    line.end[0] === otherLine.end[0] &&
    line.end[1] === otherLine.end[1] &&
    (compareColors
      ? line.color.toLowerCase() === otherLine.color.toLowerCase()
      : true);

  const reversedMapIsEqual: boolean =
    line.start[0] === otherLine.end[0] &&
    line.start[1] === otherLine.end[1] &&
    line.end[0] === otherLine.start[0] &&
    line.end[1] === otherLine.start[1] &&
    (compareColors
      ? line.color.toLowerCase() === otherLine.color.toLowerCase()
      : true);

  return standardMapIsEqual || reversedMapIsEqual;
}

function areImagesEqual(image: Image, otherImage: Image): boolean {
  return (
    image.x === otherImage.x &&
    image.y === otherImage.y &&
    image.url === otherImage.url &&
    (image.width || 0) === (otherImage.width || 0) &&
    (image.height || 0) === (otherImage.height || 0)
  );
}

function areTextsEqual(
  text: Text,
  otherText: Text,
  compareColors: boolean,
): boolean {
  return (
    text.x === otherText.x &&
    text.y === otherText.y &&
    text.text === otherText.text &&
    text.anchor === otherText.anchor &&
    (compareColors
      ? text.color.toLowerCase() === otherText.color.toLowerCase()
      : true) &&
    text.size === otherText.size &&
    text.font.toLowerCase() === otherText.font.toLowerCase()
  );
}

function arePolygonsEqual(
  polygon: Polygon,
  otherPolygon: Polygon,
  compareColors: boolean,
): boolean {
  return (
    polygon.coordinates.length === otherPolygon.coordinates.length &&
    polygon.coordinates.every(
      (point, index) => point === otherPolygon.coordinates[index],
    ) &&
    (compareColors
      ? polygon.color.toLowerCase() === otherPolygon.color.toLowerCase()
      : true) &&
    (compareColors ? polygon.outline === otherPolygon.outline : true)
  );
}
// A type alias for the key of a GraphicsShape in the canvasObjects map.
export type ShapeId = string;

export function areShapeMapsEqual(
  map: Map<ShapeId, GraphicsShape>,
  otherMap: Map<ShapeId, GraphicsShape>,
  compareColors: boolean,
): boolean {
  if (!map || !otherMap) {
    return map === otherMap;
  }
  if (map.size !== otherMap.size) {
    return false;
  }
  const unassignedShapeIds = [...map.keys()];
  for (const [, shape] of otherMap) {
    const matchingShapeIndex = unassignedShapeIds.findIndex(shapeId =>
      areShapesEqual(shape, map.get(shapeId), compareColors),
    );
    if (matchingShapeIndex === -1) {
      return false;
    }
    unassignedShapeIds.splice(matchingShapeIndex, 1);
  }
  return true;
}
